import React, { useState, useEffect } from 'react';
import { 
  Calendar, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  Save, 
  Users,
  AlertCircle,
  BookOpen,
  Loader2,
  BarChart3
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useSync } from "../../contexts/SyncContext";
import { toast } from "sonner";
import { getCurrentUser } from '@/data/users';
import apiService from '@/services/api';
import WeeklyAttendanceSummary from '@/components/attendance/WeeklyAttendanceSummary';

// St. Patrick's College Schedule - 6 Periods per day
const PERIODS = [
  { id: 'period1', name: 'Period 1', time: '7:30 AM - 8:30 AM', start: '07:30', end: '08:30', session: 'Morning' },
  { id: 'period2', name: 'Period 2', time: '8:30 AM - 9:30 AM', start: '08:30', end: '09:30', session: 'Morning' },
  { id: 'period3', name: 'Period 3', time: '10:00 AM - 11:00 AM', start: '10:00', end: '11:00', session: 'Morning' },
  { id: 'period4', name: 'Period 4', time: '11:00 AM - 12:00 PM', start: '11:00', end: '12:00', session: 'Morning' },
  { id: 'period5', name: 'Period 5', time: '1:00 PM - 1:50 PM', start: '13:00', end: '13:50', session: 'Afternoon' },
  { id: 'period6', name: 'Period 6', time: '1:50 PM - 2:40 PM', start: '13:50', end: '14:40', session: 'Afternoon' },
  { id: 'period7', name: 'Period 7', time: '2:40 PM - 3:30 PM', start: '14:40', end: '15:30', session: 'Afternoon' }
];

export default function NewRollCallPage() {
  const { addChange, getStudentsByClass } = useSync();
  const currentUser = getCurrentUser();
  
  // Detect if user is from a primary school using school_type from login
  const isPrimarySchool = currentUser?.school_type === 'primary' ||
    currentUser?.school_id?.includes('primary') || 
    currentUser?.school_id?.includes('quatui') ||
    currentUser?.school_id?.includes('quirutaro') ||
    currentUser?.school_id?.includes('quatuineala') ||
    currentUser?.department?.toLowerCase().includes('year 1') ||
    currentUser?.department?.toLowerCase().includes('year 2') ||
    currentUser?.department?.toLowerCase().includes('year 3') ||
    currentUser?.department?.toLowerCase().includes('year 4') ||
    currentUser?.department?.toLowerCase().includes('year 5') ||
    currentUser?.department?.toLowerCase().includes('year 6');
  
  // State for teacher's actual classes from API
  const [teacherClasses, setTeacherClasses] = useState([]);
  const [primaryClasses, setPrimaryClasses] = useState([]);
  const [juniorClasses, setJuniorClasses] = useState([]);
  const [seniorClasses, setSeniorClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [currentTab, setCurrentTab] = useState('primary');
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('daily'); // 'daily' for primary schools
  const [currentDate, setCurrentDate] = useState(new Date());
  const [students, setStudents] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [showWeeklySummary, setShowWeeklySummary] = useState(false);
  
  // Fetch teacher's classes from API
  useEffect(() => {
    const fetchClasses = async () => {
      try {
        setLoading(true);
        // Use skipCounts=true for faster loading since we don't need student counts here
        const classes = await apiService.getTeacherClasses(true);
        setTeacherClasses(classes);
        
        // Separate by school level
        const primary = classes.filter(c => c.year >= 1 && c.year <= 6);
        const junior = classes.filter(c => c.year >= 7 && c.year <= 10);
        const senior = classes.filter(c => c.year >= 11);
        
        // For primary schools, group by year level (attendance is per year, not per subject)
        // Create unique year levels with the first class ID for each year
        const primaryByYear = {};
        primary.forEach(c => {
          if (!primaryByYear[c.year]) {
            primaryByYear[c.year] = {
              id: c.id,
              name: `Year ${c.year}`,
              year: c.year,
              school_id: c.school_id
            };
          }
        });
        const uniquePrimaryClasses = Object.values(primaryByYear).sort((a, b) => a.year - b.year);
        
        setPrimaryClasses(uniquePrimaryClasses);
        setJuniorClasses(junior);
        setSeniorClasses(senior);
        
        // Set initial tab and class based on available classes
        if (isPrimarySchool && uniquePrimaryClasses.length > 0) {
          setCurrentTab('primary');
          setSelectedClass(uniquePrimaryClasses[0].name);
          setSelectedPeriod('daily'); // Primary schools use daily roll call
        } else if (junior.length > 0) {
          setCurrentTab('junior');
          setSelectedClass(junior[0].name);
          setSelectedPeriod('period1');
        } else if (senior.length > 0) {
          setCurrentTab('senior');
          setSelectedClass(senior[0].name);
          setSelectedPeriod('period1');
        } else if (uniquePrimaryClasses.length > 0) {
          setCurrentTab('primary');
          setSelectedClass(uniquePrimaryClasses[0].name);
          setSelectedPeriod('daily');
        }
      } catch (error) {
        console.error('Error fetching classes:', error);
        toast.error('Failed to load classes');
      } finally {
        setLoading(false);
      }
    };
    
    fetchClasses();
  }, [isPrimarySchool]);

  // Load students and attendance for selected class and period
  useEffect(() => {
    const fetchStudentsForClass = async () => {
      if (!selectedClass) return;
      
      // Determine which class list to search based on current tab
      let classesToSearch = teacherClasses;
      if (currentTab === 'primary') {
        classesToSearch = primaryClasses;
      } else if (currentTab === 'junior') {
        classesToSearch = juniorClasses;
      } else if (currentTab === 'senior') {
        classesToSearch = seniorClasses;
      }
      
      if (classesToSearch.length === 0) return;
      
      try {
        // Find the class object
        const currentClassObj = classesToSearch.find(c => c.name === selectedClass);
        
        if (!currentClassObj) {
          console.log('Class not found:', selectedClass, 'in', classesToSearch.map(c => c.name));
          setStudents([]);
          return;
        }
        
        console.log('Fetching students for class:', currentClassObj.id);
        
        // Fetch students enrolled in this specific class using the class ID
        const response = await fetch(
          `/api/classes/${currentClassObj.id}/students`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('authToken')}`
            }
          }
        );
        
        if (!response.ok) {
          throw new Error('Failed to fetch students');
        }
        
        const students = await response.json();
        console.log('Students fetched:', students.length);
        setStudents(students);
        
        // Load existing attendance from localStorage
        const attendanceKey = `${currentClassObj.id}_${selectedPeriod}_${currentDate.toISOString().split('T')[0]}`;
        const saved = localStorage.getItem(attendanceKey);
        if (saved) {
          setAttendance(JSON.parse(saved));
        } else {
          // Initialize all students as present by default
          const defaultAttendance = {};
          students.forEach(s => {
            defaultAttendance[s.id] = 'present';
          });
          setAttendance(defaultAttendance);
        }
      } catch (error) {
        console.error('Error fetching students for class:', error);
        setStudents([]);
      }
    };
    
    fetchStudentsForClass();
  }, [selectedClass, selectedPeriod, currentDate, primaryClasses, juniorClasses, seniorClasses, currentTab]);

  const handleTabChange = (tab) => {
    setCurrentTab(tab);
    // Switch to first class of new level
    if (tab === 'primary' && primaryClasses.length > 0) {
      setSelectedClass(primaryClasses[0].name);
      setSelectedPeriod('daily');
    } else if (tab === 'junior' && juniorClasses.length > 0) {
      setSelectedClass(juniorClasses[0].name);
      setSelectedPeriod('period1');
    } else if (tab === 'senior' && seniorClasses.length > 0) {
      setSelectedClass(seniorClasses[0].name);
      setSelectedPeriod('period1');
    }
  };

  const toggleAttendance = (studentId, status) => {
    setAttendance(prev => ({
      ...prev,
      [studentId]: status
    }));
  };

  const resetRollCall = () => {
    if (window.confirm('Are you sure you want to reset this roll call? All attendance data for this class and period will be cleared.')) {
      const attendanceKey = `${selectedClass}_${selectedPeriod}_${currentDate.toISOString().split('T')[0]}`;
      
      // Clear from localStorage
      localStorage.removeItem(attendanceKey);
      
      // Reset state to all present (default)
      const resetAttendance = {};
      students.forEach(student => {
        resetAttendance[student.id] = 'present';
      });
      setAttendance(resetAttendance);
      
      toast.success('Roll call has been reset successfully');
    }
  };

  const [isSaving, setIsSaving] = useState(false);

  // Helper function to get current class object
  const getCurrentClassObj = () => {
    let classesToSearch = [];
    if (currentTab === 'primary') {
      classesToSearch = primaryClasses;
    } else if (currentTab === 'junior') {
      classesToSearch = juniorClasses;
    } else if (currentTab === 'senior') {
      classesToSearch = seniorClasses;
    }
    return classesToSearch.find(c => c.name === selectedClass);
  };

  const saveAttendance = async () => {
    const currentClassObj = getCurrentClassObj();
    if (!currentClassObj) {
      toast.error('Class not found. Please refresh the page.');
      return;
    }
    
    const classId = currentClassObj.id;
    const attendanceKey = `${classId}_${selectedPeriod}_${currentDate.toISOString().split('T')[0]}`;
    
    // Save to local storage as backup
    localStorage.setItem(attendanceKey, JSON.stringify(attendance));
    
    // Prepare data for backend API
    const attendanceRecords = students.map(s => ({
      student_id: s.id,
      status: attendance[s.id] || 'present'
    }));

    const record = {
      class_id: classId,
      date: currentDate.toISOString().split('T')[0],
      period: selectedPeriod,
      attendance: attendanceRecords
    };

    // Also add to sync context for offline support
    addChange(selectedPeriod === 'daily' ? 'daily_roll_call' : 'period_roll_call', {
      class: selectedClass,
      period: selectedPeriod,
      date: currentDate.toISOString().split('T')[0],
      attendance: attendanceRecords.map(r => ({ studentId: r.student_id, status: r.status })),
      type: selectedPeriod === 'daily' ? 'daily_roll_call' : 'period_roll_call'
    });
    
    // Save to backend database
    setIsSaving(true);
    try {
      const response = await apiService.saveStudentAttendance(record);
      
      if (selectedPeriod === 'daily') {
        toast.success(`✅ ${selectedClass} - Daily attendance saved (${response.created} new, ${response.updated} updated)`);
      } else {
        toast.success(`✅ ${selectedClass} - ${PERIODS.find(p => p.id === selectedPeriod)?.name} attendance saved`);
      }
    } catch (error) {
      console.error('Failed to save attendance to database:', error);
      toast.error('⚠️ Attendance saved locally but failed to sync to database. Will retry later.');
    } finally {
      setIsSaving(false);
    }
  };

  const getStats = () => {
    const present = Object.values(attendance).filter(s => s === 'present').length;
    const absent = Object.values(attendance).filter(s => s === 'absent').length;
    const late = Object.values(attendance).filter(s => s === 'late').length;
    const total = students.length;
    const rate = total > 0 ? Math.round((present / total) * 100) : 0;
    
    return { present, absent, late, total, rate };
  };

  const stats = getStats();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 -m-6 p-6">
      <div className="space-y-6 animate-in fade-in duration-500">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between gap-4 md:items-center">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
              <BookOpen className="h-8 w-8 text-primary" />
              {isPrimarySchool ? 'Daily Roll Call' : 'Period Attendance Roll Call'}
            </h1>
            <p className="text-muted-foreground mt-1">
              {isPrimarySchool 
                ? 'Simple daily attendance tracking for primary schools' 
                : 'Period-based attendance tracking for secondary schools'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant={showWeeklySummary ? "default" : "outline"}
              size="sm"
              onClick={() => setShowWeeklySummary(!showWeeklySummary)}
              className={showWeeklySummary ? "bg-purple-600 hover:bg-purple-700" : ""}
            >
              <BarChart3 className="h-4 w-4 mr-2" />
              Weekly Summary
            </Button>
            <Badge variant="outline" className="text-base py-2 px-4">
              <Calendar className="h-4 w-4 mr-2" />
              {currentDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
            </Badge>
          </div>
        </div>

        {/* Weekly Attendance Summary */}
        {showWeeklySummary && (
          <WeeklyAttendanceSummary 
            classId={getCurrentClassObj()?.id} 
            className={selectedClass}
          />
        )}

        {/* Main Content with Tabs */}
        {loading ? (
          <div className="text-center py-12">Loading classes...</div>
        ) : (
        <Tabs value={currentTab} onValueChange={handleTabChange}>
          <TabsList className="grid w-full max-w-lg" style={{ gridTemplateColumns: `repeat(${(primaryClasses.length > 0 ? 1 : 0) + (juniorClasses.length > 0 ? 1 : 0) + (seniorClasses.length > 0 ? 1 : 0) || 1}, 1fr)` }}>
            {primaryClasses.length > 0 && (
              <TabsTrigger 
                value="primary" 
                className="text-base data-[state=active]:bg-green-600 data-[state=active]:text-white"
              >
                <Users className="h-4 w-4 mr-2" />
                Primary Classes
              </TabsTrigger>
            )}
            {juniorClasses.length > 0 && (
              <TabsTrigger 
                value="junior" 
                className="text-base data-[state=active]:bg-blue-600 data-[state=active]:text-white"
              >
                <Users className="h-4 w-4 mr-2" />
                Junior Classes
              </TabsTrigger>
            )}
            {seniorClasses.length > 0 && (
            <TabsTrigger 
              value="senior" 
              className="text-base data-[state=active]:bg-purple-600 data-[state=active]:text-white"
            >
              <Users className="h-4 w-4 mr-2" />
              Senior Classes
            </TabsTrigger>
            )}
          </TabsList>

          {/* Primary Tab Content - Simple Daily Roll Call */}
          {primaryClasses.length > 0 && (
            <TabsContent value="primary" className="space-y-6 mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Left Sidebar - Controls */}
              <Card className="lg:col-span-1 border-green-200">
                <CardHeader className="bg-green-50 rounded-t-lg">
                  <CardTitle className="text-lg text-green-800">Daily Roll Call</CardTitle>
                  <CardDescription>Simple once-a-day attendance</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6 pt-4">
                  {/* Class Selector */}
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-muted-foreground uppercase">Select Class</label>
                    <Select value={selectedClass} onValueChange={(val) => { setSelectedClass(val); setSelectedPeriod('daily'); }}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {primaryClasses.map(c => (
                          <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Stats */}
                  <div className="space-y-3 pt-4 border-t">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Total Students</span>
                      <Badge variant="outline">{stats.total}</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Present</span>
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        {stats.present}
                      </Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Late</span>
                      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                        {stats.late}
                      </Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Absent</span>
                      <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                        {stats.absent}
                      </Badge>
                    </div>
                    <div className="pt-2 border-t">
                      <div className="text-xs text-muted-foreground mb-1">Attendance Rate</div>
                      <div className="text-2xl font-bold text-green-600">{stats.rate}%</div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-2">
                    <Button onClick={saveAttendance} className="w-full bg-green-600 hover:bg-green-700" size="lg" disabled={isSaving}>
                      {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                      {isSaving ? 'Saving...' : 'Save Daily Roll'}
                    </Button>
                    <Button 
                      onClick={resetRollCall} 
                      variant="outline" 
                      className="w-full border-red-300 text-red-600 hover:bg-red-50" 
                      size="lg"
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Reset
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Main Content - Student List */}
              <Card className="lg:col-span-3">
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-xl">
                      {selectedClass || 'Select a Class'} - Daily Attendance
                    </CardTitle>
                    <Badge className="bg-green-100 text-green-800 border-green-300">
                      {currentDate.toLocaleDateString('en-US', { weekday: 'long' })}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  {students.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No students enrolled in this class yet</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12">#</TableHead>
                          <TableHead>Student Name</TableHead>
                          <TableHead className="text-center w-28">Present</TableHead>
                          <TableHead className="text-center w-28">Late</TableHead>
                          <TableHead className="text-center w-28">Absent</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {students.map((student, idx) => (
                          <TableRow key={student.id} className="hover:bg-gray-50">
                            <TableCell className="font-medium text-muted-foreground">{idx + 1}</TableCell>
                            <TableCell className="font-medium">{student.name}</TableCell>
                            <TableCell className="text-center">
                              <Button
                                size="sm"
                                variant={attendance[student.id] === 'present' || !attendance[student.id] ? 'default' : 'outline'}
                                className={attendance[student.id] === 'present' || !attendance[student.id] ? 'bg-green-600 hover:bg-green-700' : ''}
                                onClick={() => toggleAttendance(student.id, 'present')}
                              >
                                <CheckCircle2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                            <TableCell className="text-center">
                              <Button
                                size="sm"
                                variant={attendance[student.id] === 'late' ? 'default' : 'outline'}
                                className={attendance[student.id] === 'late' ? 'bg-amber-500 hover:bg-amber-600' : ''}
                                onClick={() => toggleAttendance(student.id, 'late')}
                              >
                                <Clock className="h-4 w-4" />
                              </Button>
                            </TableCell>
                            <TableCell className="text-center">
                              <Button
                                size="sm"
                                variant={attendance[student.id] === 'absent' ? 'default' : 'outline'}
                                className={attendance[student.id] === 'absent' ? 'bg-red-600 hover:bg-red-700' : ''}
                                onClick={() => toggleAttendance(student.id, 'absent')}
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          )}

          {/* Junior Tab Content */}
          {juniorClasses.length > 0 && (
            <TabsContent value="junior" className="space-y-6 mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Left Sidebar - Controls */}
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle className="text-lg">Controls</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Class Selector */}
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-muted-foreground uppercase">Select Class</label>
                    <Select value={selectedClass} onValueChange={setSelectedClass}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {juniorClasses.map(c => (
                          <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Period Selector */}
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-muted-foreground uppercase">Select Period</label>
                    <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PERIODS.map(p => (
                          <SelectItem key={p.id} value={p.id}>
                            <div className="flex flex-col">
                              <span className="font-medium">{p.name}</span>
                              <span className="text-xs text-muted-foreground">{p.time}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Stats */}
                  <div className="space-y-3 pt-4 border-t">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Total Students</span>
                      <Badge variant="outline">{stats.total}</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Present</span>
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        {stats.present}
                      </Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Absent</span>
                      <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                        {stats.absent}
                      </Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Late</span>
                      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                        {stats.late}
                      </Badge>
                    </div>
                    <div className="pt-2 border-t">
                      <div className="text-xs text-muted-foreground mb-1">Attendance Rate</div>
                      <div className="text-2xl font-bold text-primary">{stats.rate}%</div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-2">
                    <Button onClick={saveAttendance} className="w-full" size="lg">
                      <Save className="h-4 w-4 mr-2" />
                      Save Attendance
                    </Button>
                    <Button 
                      onClick={resetRollCall} 
                      variant="outline" 
                      className="w-full border-red-300 text-red-600 hover:bg-red-50" 
                      size="lg"
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Reset Roll Call
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Right Side - Student List */}
              <Card className="lg:col-span-3">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    {selectedClass} - {PERIODS.find(p => p.id === selectedPeriod)?.name}
                  </CardTitle>
                  <CardDescription>
                    Mark attendance for each student
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {students.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <AlertCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No students found in this class</p>
                      <p className="text-sm mt-1">Please add students to continue</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12">#</TableHead>
                          <TableHead>Student Name</TableHead>
                          <TableHead className="text-center">Attendance Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {students.map((student, index) => (
                          <TableRow key={student.id}>
                            <TableCell className="font-medium text-muted-foreground">
                              {index + 1}
                            </TableCell>
                            <TableCell className="font-medium">{student.name}</TableCell>
                            <TableCell>
                              <div className="flex justify-center gap-2">
                                <Button
                                  size="sm"
                                  variant={attendance[student.id] === 'present' ? 'default' : 'outline'}
                                  onClick={() => toggleAttendance(student.id, 'present')}
                                  className={attendance[student.id] === 'present' ? 'bg-green-600 hover:bg-green-700' : ''}
                                >
                                  <CheckCircle2 className="h-4 w-4 mr-1" />
                                  Present
                                </Button>
                                <Button
                                  size="sm"
                                  variant={attendance[student.id] === 'late' ? 'default' : 'outline'}
                                  onClick={() => toggleAttendance(student.id, 'late')}
                                  className={attendance[student.id] === 'late' ? 'bg-amber-600 hover:bg-amber-700' : ''}
                                >
                                  <Clock className="h-4 w-4 mr-1" />
                                  Late
                                </Button>
                                <Button
                                  size="sm"
                                  variant={attendance[student.id] === 'absent' ? 'default' : 'outline'}
                                  onClick={() => toggleAttendance(student.id, 'absent')}
                                  className={attendance[student.id] === 'absent' ? 'bg-red-600 hover:bg-red-700' : ''}
                                >
                                  <XCircle className="h-4 w-4 mr-1" />
                                  Absent
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          )}

          {/* Senior Tab Content */}
          <TabsContent value="senior" className="space-y-6 mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Left Sidebar - Controls */}
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle className="text-lg">Controls</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Class Selector */}
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-muted-foreground uppercase">Select Class</label>
                    <Select value={selectedClass} onValueChange={setSelectedClass}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {seniorClasses.map(c => (
                          <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Period Selector */}
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-muted-foreground uppercase">Select Period</label>
                    <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PERIODS.map(p => (
                          <SelectItem key={p.id} value={p.id}>
                            <div className="flex flex-col">
                              <span className="font-medium">{p.name}</span>
                              <span className="text-xs text-muted-foreground">{p.time}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Stats */}
                  <div className="space-y-3 pt-4 border-t">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Total Students</span>
                      <Badge variant="outline">{stats.total}</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Present</span>
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        {stats.present}
                      </Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Absent</span>
                      <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                        {stats.absent}
                      </Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Late</span>
                      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                        {stats.late}
                      </Badge>
                    </div>
                    <div className="pt-2 border-t">
                      <div className="text-xs text-muted-foreground mb-1">Attendance Rate</div>
                      <div className="text-2xl font-bold text-primary">{stats.rate}%</div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-2">
                    <Button onClick={saveAttendance} className="w-full" size="lg">
                      <Save className="h-4 w-4 mr-2" />
                      Save Attendance
                    </Button>
                    <Button 
                      onClick={resetRollCall} 
                      variant="outline" 
                      className="w-full border-red-300 text-red-600 hover:bg-red-50" 
                      size="lg"
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Reset Roll Call
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Right Side - Student List */}
              <Card className="lg:col-span-3">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    {selectedClass} - {PERIODS.find(p => p.id === selectedPeriod)?.name}
                  </CardTitle>
                  <CardDescription>
                    Mark attendance for each student
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {students.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <AlertCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No students found in this class</p>
                      <p className="text-sm mt-1">Please add students to continue</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12">#</TableHead>
                          <TableHead>Student Name</TableHead>
                          <TableHead className="text-center">Attendance Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {students.map((student, index) => (
                          <TableRow key={student.id}>
                            <TableCell className="font-medium text-muted-foreground">
                              {index + 1}
                            </TableCell>
                            <TableCell className="font-medium">{student.name}</TableCell>
                            <TableCell>
                              <div className="flex justify-center gap-2">
                                <Button
                                  size="sm"
                                  variant={attendance[student.id] === 'present' ? 'default' : 'outline'}
                                  onClick={() => toggleAttendance(student.id, 'present')}
                                  className={attendance[student.id] === 'present' ? 'bg-green-600 hover:bg-green-700' : ''}
                                >
                                  <CheckCircle2 className="h-4 w-4 mr-1" />
                                  Present
                                </Button>
                                <Button
                                  size="sm"
                                  variant={attendance[student.id] === 'late' ? 'default' : 'outline'}
                                  onClick={() => toggleAttendance(student.id, 'late')}
                                  className={attendance[student.id] === 'late' ? 'bg-amber-600 hover:bg-amber-700' : ''}
                                >
                                  <Clock className="h-4 w-4 mr-1" />
                                  Late
                                </Button>
                                <Button
                                  size="sm"
                                  variant={attendance[student.id] === 'absent' ? 'default' : 'outline'}
                                  onClick={() => toggleAttendance(student.id, 'absent')}
                                  className={attendance[student.id] === 'absent' ? 'bg-red-600 hover:bg-red-700' : ''}
                                >
                                  <XCircle className="h-4 w-4 mr-1" />
                                  Absent
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
        )}
      </div>
    </div>
  );
}
